import { Injectable } from '@angular/core';
import {
  differenceInHours,
  differenceInMinutes,
  format,
  isSameDay,
  parseISO,
} from 'date-fns';
import { firstValueFrom, map } from 'rxjs';
import { BaseService } from 'src/app/helpers/base.service';
import {
  iAttendanceDto,
  iAttendanceExport,
  iAttendanceHistoryDto,
  iAttendancePayrollSummary,
  iAttendancePenaltyResponse,
  iAttendanceRequest,
  iAttendanceResponse,
  iAttendanceSummaryResult,
  iEmployeeAttendanceSummaryRequest,
  iEmployeeAttendanceSummaryResponse,
  iExportAttendanceReq,
  iGetAttendanceRequest,
} from 'src/app/helpers/Interfaces/attendance';
import { iEmployeeResultDto } from 'src/app/helpers/Interfaces/employee';
import { iEmpHoursResultDto } from 'src/app/helpers/Interfaces/shifts';
import { environment } from 'src/environments/environment.development';
import * as xlsx from 'xlsx';
import { ShiftsService } from '../../setup/shifts/shifts.service';
import { iPayrollResponse } from 'src/app/helpers/Interfaces/payroll';
import { Dictionary } from 'lodash';
import { HttpParams } from '@angular/common/http';
interface iAttendanceResult {
  attendance: iAttendanceDto[];
  summary: iAttendanceSummaryResult;
  payroll: iAttendancePayrollSummary;
}
@Injectable({
  providedIn: 'root',
})
export class AttendanceService extends BaseService {
  constructor(private _shiftServ: ShiftsService) {
    super();
  }
  private _checkAttendance = `${environment.base_url}/api/Attendance/CheckAttendance`;
  private _markAttendance = `${environment.base_url}/api/Attendance/Add`;
  private _markAttendanceQr = `${environment.base_url}/api/Attendance/AddForQR`;
  private _getAttendance = `${environment.base_url}/api/Attendance/GetByEmployee`;
  private _getAttendanceCSV = `${environment.base_url}/api/Attendance/ExportAttendances`;
  private _exportAttendance = `${environment.base_url}/api/Attendance/ExcelExportAttendances`;
  private _getManagementDashboardRecord = `${environment.base_url}/api/Attendance/GetAttendaceWithProj`;
  private _getAttendanceSummary = `${environment.base_url}/api/Attendance/GetAttendanceSummary`;
  private _addOrUpdateAttendance = `${environment.base_url}/api/Attendance/AddOrUpdateAttendance`;
  private _getEmployeeAttendanceSummary = `${environment.base_url}/api/Attendance/GetEmployeeAttendanceSummary`;
  private _getAttendancePenalty = `${environment.payroll_base_url}/api/AttendancePenalty/Get`;
  private _getHistory = `${environment.base_url}/api/Attendance/GetAttendanceHistory`

  private _GetAttendancePenalty() {
    return this._http
      .post<
        iPayrollResponse<{
          data: iAttendancePenaltyResponse[];
          totalRecords: number;
        }>
      >(
        `${this._getAttendancePenalty}`,
        {},
        {
          responseType: 'json',
        }
      )
      .pipe(map((res) => res.result.data));
  }

  public CheckAttendance(employeeId: string) {
    return this._http.get<any>(
      `${this._checkAttendance}?EmployeeId=${employeeId}`,
      {
        responseType: 'json',
      }
    );
  }
  public MarkAttendance(payload: any) {
    return this._http.post<any>(this._markAttendance, payload, {
      responseType: 'json',
    });
  }
  public markAttendance(payload: any) {
    return this._http.post<any>(this._markAttendanceQr, payload, {
      responseType: 'json',
    });
  }
  public GetAttendanceSummary(payload: iGetAttendanceRequest) {
    return this._http.get<iAttendanceSummaryResult>(
      `${this._getAttendanceSummary}?id=${payload.id}&startDate=${payload.sd}&endDate=${payload.ed}`
    );
  }
  public GetAttendance(
    payload: iGetAttendanceRequest,
    employee: iEmployeeResultDto[]
  ) {
    const params: any = { startDate: payload.sd!, endDate: payload.ed! };
    if (payload.departmentId) params['departmentId'] = payload.departmentId;
    if (payload.id) params['id'] = payload.id;
    return this._http
      .get<iAttendanceResponse[]>(`${this._getAttendance}`, { params })
      .pipe(
        map(this.adjustTimezones),
        map(this.processAttendanceData(employee))
      );
  }
  private adjustTimezones = (
    records: iAttendanceResponse[]
  ): iAttendanceResponse[] => {
    const adjustTime = (time: string): string => {
      const timeWithoutZ = time.replace(/[zZ]/g, '');
      const date = parseISO(timeWithoutZ);
      const adjustedTime = date.getTime() - date.getTimezoneOffset() * 60000;
      return `${format(adjustedTime, 'yyyy-MM-dd')}T${format(
        adjustedTime,
        'HH:mm:ss'
      )}`;
    };

    return records.map((record) => ({
      ...record,
      shift: record.shift
        ? {
          ...record.shift,
          startTime: record.shift.startTime
            ? adjustTime(record.shift.startTime)
            : null,
          endTime: record.shift.endTime
            ? adjustTime(record.shift.endTime)
            : null,
        }
        : null,
    }));
  };
  private processAttendanceData =
    (employee: iEmployeeResultDto[]) =>
      async (records: iAttendanceResponse[]): Promise<iAttendanceResult> => {
        const summary = {
          totalHours: 0,
          hoursWorked: 0,
          totalDays: 0,
          presentDays: 0,
          totalOvertime: 0,
        };

        const payroll: iAttendancePayrollSummary = {
          present: 0,
          absent: 0,
          late: 0,
          early: 0,
          hd: 0,
          leaveCount: 0,
          leaves: '',
        };

        const leaves = records.filter((r) => r.flag === 2 || r.flag === 3);
        payroll.leaveCount = leaves.length;
        payroll.leaves = this.formatLeaves(leaves);
        const penalty = await firstValueFrom(this._GetAttendancePenalty());
        // let empHours: iEmpHoursResultDto[] = [];
        // if (employee.some((x) => x.isHourly && x.workingHours)) {
        //   empHours = await firstValueFrom(
        //     this._shiftServ.empHoursGetByEmployee(
        //       employee
        //         .filter((x) => x.isHourly && x.workingHours)
        //         .map((x) => x.id!)
        //     )
        //   );
        // }

        const normalizeWorkingHours = (
          wh?: iEmpHoursResultDto | null
        ): iEmpHoursResultDto[] => (wh ? [wh] : []);

        const attendance = records.map((record) => {
          const emp = employee.find((x) => x.id === record.employeeId);
          // const eh = empHours.filter((x) => x.employeeId == record.employeeId);
          if (emp)
            return this.processAttendanceRecord(
              record,
              summary,
              payroll,
              emp,
              // eh,
              normalizeWorkingHours(record.workingHours),
              penalty
            );
          else
            return {
              ...record,
              earlyTime: '',
              lateTime: '',
              overtime: '',
              totalTime: '',
              date: format(new Date(record.signIn), 'dd-MM-yyyy'),
              day: format(new Date(record.signIn), 'eeee'),
              rawSignIn: record.signIn,
              rawSignOut: record.signOut,
              hd: false,
            } as iAttendanceDto;
        });

        return {
          attendance: attendance.reverse(),
          summary: this.calculateSummary(summary),
          payroll,
        };
      };
  private formatLeaves(leaves: iAttendanceResponse[]): string {
    const leaveTypes = leaves.reduce((acc, att) => {
      let name = 'L';
      if (att.leaveName) {
        const sp = att.leaveName
          .replace(/[^a-zA-Z0-9 ]/g, '')
          .split(' ')
          .splice(0, 2);
        name = sp
          .map((word) => (word[0] ? word[0].toUpperCase() : ''))
          .join('');
      }
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    return Object.entries(leaveTypes)
      .map(([leave, count]) => `${count}${leave}`)
      .join(', ')
      .trim();
  }
  private processAttendanceRecord(
    record: iAttendanceResponse,
    summary: any,
    payroll: iAttendancePayrollSummary,
    employee: iEmployeeResultDto,
    workingHours: iEmpHoursResultDto[],
    penalty: iAttendancePenaltyResponse[]
  ) {
    const dto: iAttendanceDto = {
      ...record,
      earlyTime: '',
      lateTime: '',
      overtime: '',
      totalTime: '',
      date: format(new Date(record.signIn), 'dd-MM-yyyy'),
      day: format(new Date(record.signIn), 'eeee'),
      rawSignIn: record.signIn,
      rawSignOut: record.signOut,
      hd: false,
      username: employee.userName || null,
    };
    if (record.flag !== 1) summary.totalDays++;
    if (record.flag !== 1 && record.flag !== 5 && record.flag !== 3)
      summary.presentDays++;
    if (record.flag === 0) payroll.present++;
    if (record.flag === 5) payroll.absent++;
    if (record.flag === 2) summary.presentDays -= 0.5;

    if (employee.isHourly && employee.workingHours) {
      this.processWorkingHoursDetails(
        record,
        dto,
        summary,
        payroll,
        employee,
        workingHours,
        penalty
      );
    } else if (record.shift?.startTime && record.shift.endTime) {
      this.processShiftDetails(record, dto, summary, payroll, penalty);
    }

    dto.signIn = format(new Date(record.signIn), 'hh:mm:ss a');
    dto.signOut = record.signOut
      ? format(new Date(record.signOut), 'hh:mm:ss a')
      : '---';

    return dto;
  }
  private processWorkingHoursDetails(
    record: iAttendanceResponse,
    dto: iAttendanceDto,
    summary: any,
    payroll: iAttendancePayrollSummary,
    employee: iEmployeeResultDto,
    workingHours: iEmpHoursResultDto[],
    penalty: iAttendancePenaltyResponse[]
  ) {
    const today = new Date().toISOString().split('T')[0];
    const latePenalty = penalty.find((x) => x.flag == 3)?.penaltyHalfDay || 30;
    const getTime = (timeString: string) =>
      timeString.split('T')[1].split('.')[0];
    const signIn = new Date(`${today}T${getTime(record.signIn)}`);
    const signOut = record.signOut
      ? new Date(`${today}T${getTime(record.signOut)}`)
      : undefined;
    let totalHours = this.processEmpHours(
      employee,
      workingHours,
      new Date(record.signIn)
    );
    if (totalHours == 0 && record.shift) {
      const getTimeAlt = (timeString: string) =>
        `${format(timeString, 'HH:mm:')}00`;
      const shiftIn = new Date(
        `${today}T${getTimeAlt(record.shift.startTime!)}`
      );
      const shiftOut = new Date(
        `${today}T${getTimeAlt(record.shift.endTime!)}`
      );
      totalHours = differenceInHours(shiftOut, shiftIn);
    }
    const totalMinutes = totalHours * 60;
    if (![1, 3].includes(record.flag)) {
      summary.totalHours += totalMinutes / 60;
    }

    let isHalfDay = false;
    if (
      signIn &&
      !signOut &&
      record.flag === 0 &&
      !isSameDay(record.signIn, today)
    ) {
      isHalfDay = true;
      dto.hd = true;
      payroll.hd++;
      payroll.present--;
    }

    if (signOut) {
      const workedMinutes = differenceInMinutes(signOut, signIn);
      if (
        workedMinutes < totalMinutes - latePenalty &&
        record.flag === 0 &&
        !isHalfDay
      ) {
        isHalfDay = true;
        dto.hd = true;
        payroll.hd++;
        payroll.present--;
      }
      if ([0, 2, 6].includes(record.flag)) {
        summary.hoursWorked += workedMinutes / 60;
      }
      if (workedMinutes > totalMinutes) {
        const overtimeMinutes = workedMinutes - totalMinutes;
        summary.totalOvertime += overtimeMinutes / 60;
        dto.overtime = this.msToTime(overtimeMinutes * 60000);
      }

      dto.totalTime = this.msToTime(workedMinutes * 60000);
    }
  }
  private processShiftDetails(
    record: iAttendanceResponse,
    dto: iAttendanceDto,
    summary: any,
    payroll: iAttendancePayrollSummary,
    penalty: iAttendancePenaltyResponse[]
  ): void {
    const today = new Date().toISOString().split('T')[0];
    const latePenalty = penalty.find((x) => x.flag == 1)?.penaltyHalfDay || 150;
    const earlyPenalty = penalty.find((x) => x.flag == 2)?.penaltyHalfDay || 30;

    const getTime = (timeString: string) =>
      timeString.split('T')[1].split('.')[0];
    const getTimeAlt = (timeString: string) =>
      `${format(timeString, 'HH:mm:')}00`;
    const shiftIn = new Date(
      `${today}T${getTimeAlt(record.shift!.startTime!)}`
    );
    const shiftOut = new Date(`${today}T${getTimeAlt(record.shift!.endTime!)}`);
    const earlyTimeOut = parseInt(record.shift!.earlyTimeOut || '0');
    const lateTimeIn = parseInt(record.shift!.lateTimeIn || '0');
    const signIn = new Date(`${today}T${getTime(record.signIn)}`);
    const signOut = record.signOut
      ? new Date(`${today}T${getTime(record.signOut)}`)
      : undefined;

    const shiftTotalMinutes = differenceInMinutes(shiftOut, shiftIn);
    if (![1, 3].includes(record.flag)) {
      summary.totalHours += shiftTotalMinutes / 60;
    }

    let isHalfDay = false;
    if (
      signIn &&
      !signOut &&
      record.flag === 0 &&
      !isSameDay(record.signIn, today)
    ) {
      isHalfDay = true;
      dto.hd = true;
      payroll.hd++;
      payroll.present--;
    }

    if (
      record.shift!.lateTimeIn &&
      !record.leaveName &&
      ![1, 2, 3, 5, 6].includes(record.flag)
    ) {
      const lateMinutes = differenceInMinutes(signIn, shiftIn);
      if (lateMinutes > lateTimeIn) {
        if (lateMinutes > latePenalty && record.flag === 0 && !isHalfDay) {
          isHalfDay = true;
          dto.hd = true;
          payroll.hd++;
          payroll.present--;
        } else if (record.flag !== 6 && !isHalfDay) {
          payroll.late++;
        }
        dto.lateTime = this.msToTime(lateMinutes * 60000);
      }
    }

    if (signOut) {
      if (
        record.shift!.earlyTimeOut &&
        !record.leaveName &&
        ![1, 2, 3, 5, 6].includes(record.flag)
      ) {
        const earlyMinutes = differenceInMinutes(shiftOut, signOut);
        if (earlyMinutes > earlyTimeOut) {
          if (earlyMinutes > earlyPenalty && record.flag === 0 && !isHalfDay) {
            isHalfDay = true;
            dto.hd = true;
            payroll.hd++;
            payroll.present--;
          } else if (record.flag !== 6 && !isHalfDay) {
            payroll.early++;
          }
          dto.earlyTime = this.msToTime(earlyMinutes * 60000);
        }
      }

      const workedMinutes = differenceInMinutes(signOut, signIn);
      if ([0, 2].includes(record.flag)) {
        summary.hoursWorked += workedMinutes / 60;
      }

      if (workedMinutes > shiftTotalMinutes) {
        const overtimeMinutes = workedMinutes - shiftTotalMinutes;
        summary.totalOvertime += overtimeMinutes / 60;
        dto.overtime = this.msToTime(overtimeMinutes * 60000);
      }

      dto.totalTime = this.msToTime(workedMinutes * 60000);
    }
  }
  private calculateSummary(summary: any): iAttendanceSummaryResult {
    const t = summary.hoursWorked / summary.totalHours;
    const overall = Number.isNaN(t)
      ? 0
      : ((summary.hoursWorked / summary.totalHours) * 100).toFixed(2);
    return {
      overall: overall + '%',
      presentDays: summary.presentDays.toString(),
      totalDays: summary.totalDays.toString(),
      totalHours: summary.totalHours.toFixed(2),
      totalHoursWorked: summary.hoursWorked.toFixed(2),
      totalOvertime: summary.totalOvertime.toFixed(2),
    };
  }
  private processEmpHours(
    employee: iEmployeeResultDto,
    workingHours: iEmpHoursResultDto[],
    signIn: Date
  ) {
    let hours = 0;
    if (workingHours && workingHours.length > 0) {
      for (const wh of workingHours) {
        if (!wh.startDate || !wh.endDate) {
          hours = wh.hours!;
        } else {
          const empStartDate = new Date(wh.startDate!);

          const empEndDate = new Date(wh.endDate!);
          if (signIn >= empStartDate && signIn <= empEndDate) {
            hours = wh.hours!;
          } else {
            hours = employee.workingHours ? employee.workingHours : 0;
          }
        }
      }
    }
    return hours;
  }
  public GetAttendanceCSV(payload: any) {
    return this._http.get<any>(
      `${this._getAttendanceCSV}?EmpId=${payload.id}&startDate=${payload.sd}&endDate=${payload.ed}`,
      {
        responseType: 'arraybuffer',
      } as any
    );
  }
  public GetManagementDashboardRecord(empId?: string) {
    return this._http
      .get<any[]>(
        `${this._getManagementDashboardRecord
        }?date=${new Date().toISOString()}${empId ? `&EmpId=${empId}` : ''}`
      )
      .pipe(
        map((v) => {
          v.forEach((x) => {
            x.employee = x.employee.split(' ');
            x.profilePic = `${environment.base_url}/${x.profilePic}`;
            if (x.signIn) x.signIn = format(parseISO(x.signIn), 'HH:mm:ss a');
            if (x.signOut)
              x.signOut = format(parseISO(x.signOut), 'HH:mm:ss a');
          });
          return v;
        })
      );
  }
  msToTime(duration: any) {
    // const milliseconds = parseInt((duration % 1000) as any);
    const seconds = Math.floor((duration / 1000) % 60);
    const minutes = Math.floor((duration / (1000 * 60)) % 60);
    const hours = Math.floor(duration / (1000 * 60 * 60));

    return (
      `${hours != 0 ? hours + `h ` : ''}` +
      `${minutes != 0 ? minutes + `m ` : ''}` +
      `${seconds != 0 ? seconds + `s` : ''}`
    );
  }
  exportAttendance(payload: iExportAttendanceReq) {
    return this._http.post(`${this._exportAttendance}`, payload, {
      responseType: 'arraybuffer',
    });
  }
  addOrUpdateAttendance(payload: iAttendanceRequest) {
    const date = new Date();
    const adjustTimezone = (time: string) => {
      const timeWithoutZ = time.replace(/[zZ]/g, '');
      const date = parseISO(timeWithoutZ);
      const timeZoneOffset = date.getTimezoneOffset() * 60000; // Convert to milliseconds
      return date.getTime() - timeZoneOffset;
    };
    return this._http
      .post<iAttendanceResponse>(`${this._addOrUpdateAttendance}`, payload, {
        responseType: 'json',
      })
      .pipe(
        map((x) => {
          if (x.shift?.startTime)
            x.shift.startTime = `${format(
              adjustTimezone(x.shift.startTime),
              'yyyy-MM-dd'
            )}T${format(adjustTimezone(x.shift.startTime), 'HH:mm:ss')}`;
          if (x.shift?.endTime)
            x.shift.endTime = `${format(
              adjustTimezone(x.shift.endTime),
              'yyyy-MM-dd'
            )}T${format(adjustTimezone(x.shift.endTime), 'HH:mm:ss')}`;
          return x;
        }),
        map((x) => {
          const dto: iAttendanceDto = {
            ...x,
            earlyTime: '',
            lateTime: '',
            overtime: '',
            totalTime: '',
            date: '',
            day: '',
            rawSignIn: x.signIn,
            rawSignOut: x.signOut,
            hd: false,
            username: null,
          };
          if (x.shift && x.shift.startTime && x.shift.endTime) {
            const shiftIn = `${date.toISOString().split('T')[0]}T${x.shift.startTime.split('T')[1]
              }`.split('.')[0];
            const shiftOut = `${date.toISOString().split('T')[0]}T${x.shift.endTime.split('T')[1]
              }`.split('.')[0];

            const shiftTotal = differenceInMinutes(shiftOut, shiftIn);

            var inn = `${date.toISOString().split('T')[0]}T${x.signIn.split('T')[1]
              }`.split('.')[0];
            var out;
            if (x.signOut) {
              out = `${date.toISOString().split('T')[0]}T${x.signOut.split('T')[1]
                }`.split('.')[0];
            }

            if (x.shift.lateTimeIn) {
              const inDiff = differenceInMinutes(inn, shiftIn);
              if (inDiff > 0) {
                dto.lateTime = this.msToTime(inDiff * 60000);
              }
            }

            if (out) {
              // if (calc < shiftTotal) {
              //   dto.earlyTime = this.msToTime((shiftTotal - calc) * 60000);
              // }
              if (x.shift.earlyTimeOut) {
                const outDiff = differenceInMinutes(shiftOut, out);
                if (outDiff > 0) {
                  dto.earlyTime = this.msToTime(outDiff * 60000);
                }
              }
              const calc = differenceInMinutes(out, inn);
              if (calc > shiftTotal) {
                dto.overtime = this.msToTime((calc - shiftTotal) * 60000);
              }
              dto.totalTime = this.msToTime(calc * 60000);
            }
          }
          dto.date = format(new Date(x.signIn), 'dd-MM-yyyy');
          dto.day = format(new Date(x.signIn), 'eeee');
          dto.signIn = format(new Date(x.signIn), 'hh:mm:ss a');
          dto.signOut = x.signOut
            ? format(new Date(x.signOut), 'hh:mm:ss a')
            : '---';
          return dto;
        })
      );
  }
  selectedExport(
    attendance: iAttendanceDto[],
    title: string,
    machineId: string,
    summary?: iAttendanceSummaryResult,
  ) {
    const sheet = xlsx.utils.json_to_sheet([
      ...this._processAttendance(attendance, machineId),
      {
        EMPLOYEE: '',
        'MACHINE ID': '',
        DATE: '',
        DAY: '',
        'TIME IN': '',
        ...(summary ? { 'TIME OUT': 'Total Days' } : { 'TIME OUT': '' }),
        ...(summary && { TOTAL: summary.presentDays })
      },
      {
        EMPLOYEE: '',
        'MACHINE ID': '',
        DATE: '',
        DAY: '',
        'TIME IN': '',
        ...(summary ? { 'TIME OUT': 'Total Hours' } : { 'TIME OUT': '' }),
        ...(summary && { TOTAL: this.msToTime(parseFloat(summary.totalHoursWorked) * 60 * 60000) }),
      },
      {
        EMPLOYEE: '',
        'MACHINE ID': '',
        DATE: '',
        DAY: '',
        'TIME IN': '',
        ...(summary ? { 'TIME OUT': 'Total Overtime' } : { 'TIME OUT': '' }),
        ...(summary && { TOTAL: this.msToTime(parseFloat(summary.totalOvertime) * 60 * 60000) }),
      },
    ]);
    const book = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(book, sheet);
    const fileName = `${title}.xlsx`.trim();

    xlsx.writeFileXLSX(book, fileName);
  }
  private _processAttendance(
    attendance: iAttendanceDto[],
    machineId: string
  ): iAttendanceExport[] {
    return [
      ...attendance.map((att) => ({
        EMPLOYEE: att.employee || '---',
        'MACHINE ID': att.machineId || machineId || '---',
        DATE: att.date,
        DAY: att.day,
        'TIME IN': `${att.flag == 1 || att.flag == 3 || att.flag == 5
          ? '---'
          : att.signIn || '---'
          } ${att.lateTime && !(att.flag == 1 || att.flag == 3 || att.flag == 5)
            ? `(${att.lateTime} Late)`
            : ''
          }`,
        'TIME OUT': `${att.signOut ?? '---'} ${att.earlyTime ? `(${att.earlyTime} Early)` : ''
          }`,
        TOTAL: `${att.totalTime} ${att.overtime ? `(${att.overtime} Overtime) ` : ''
          }${att.flag == 3 || att.flag == 2 ? `${att.leaveName} ` : ''}${att.flag == 1 ? `${att.holidayName} ` : ''
          }${att.flag == 2 ? 'Half Day ' : ''}${att.flag == 5 ? 'Absent ' : ''}`,
      })),
      {
        EMPLOYEE: '',
        'MACHINE ID': '',
        DATE: '',
        DAY: '',
        'TIME IN': '',
        'TIME OUT': '',
        TOTAL: '',
      },
      {
        EMPLOYEE: '',
        'MACHINE ID': '',
        DATE: '',
        DAY: '',
        'TIME IN': '',
        'TIME OUT': '',
        TOTAL: '',
      },
    ];
  }
  getEmployeeAttendanceSummary(payload: iEmployeeAttendanceSummaryRequest) {
    return this._http.post<iEmployeeAttendanceSummaryResponse[]>(
      this._getEmployeeAttendanceSummary,
      payload,
      {
        responseType: 'json',
      }
    );
  }
  private _processExportEmployeeAttendanceSummary(
    summary: iEmployeeAttendanceSummaryResponse[]
  ) {
    return summary.map((x) => ({
      Employee: `${x.employeeName}${x.username ? ` (${x.username})` : ''}`,
      'Total Hours': x.totalHours,
      'Hours Consumed': x.hoursConsumed,
      'Hours Short': x.hoursShort,
      'Assigned Tasks': x.totalTask,
      'Completed Tasks': x.completedTask,
      'Remaining Task': x.remainingTask,
    }));
  }
  exportEmployeeAttendanceSummary(
    summary: iEmployeeAttendanceSummaryResponse[],
    title: string
  ) {
    const sheet = xlsx.utils.json_to_sheet([
      ...this._processExportEmployeeAttendanceSummary(summary),
    ]);
    const book = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(book, sheet);
    const fileName = `${title}.xlsx`.trim();
    xlsx.writeFileXLSX(book, fileName);
  }

  getAttendanceHistory(employeeId: string, startDate: string, endDate: string) {
    const params = new HttpParams()
      .set('employeeId', employeeId)
      .set('startDate', startDate)
      .set('endDate', endDate)
    return this._http.get<iAttendanceHistoryDto[]>(this._getHistory, { params });
  }
}
